/**
 * @perkos/perkos-a2a -- OpenClaw Plugin
 *
 * Agent-to-Agent (A2A) protocol communication plugin.
 * Adds tools for agents to discover peers, send tasks, and check task status.
 * Supports direct HTTP, relay-based NAT traversal, and session injection.
 *
 * v0.8.0: Uses enqueueSystemEvent + WebSocket wake for immediate task processing.
 */

import { A2AServer, detectNetworking } from "./server.js";
import { randomUUID } from "crypto";
import { completeTaskWithReply, generateRuntimeReply } from "./runtime-reply.js";
import { tryHandleAgenticTask } from "./agentic-actions.js";
import { ChatClient } from "./chat-client.js";
import type { A2APluginConfig } from "./types.js";
import type {
  ChannelJoinFrame,
  ChatDeliverFrame,
  ChatMessageInboundFrame,
} from "./chat-types.js";
export { A2AServer, detectNetworking } from "./server.js";
export { RelayHub } from "./relay.js";
export { RelayClient } from "./relay-client.js";
export { ChatClient } from "./chat-client.js";
export { ChatStore } from "./chat-store.js";
export * from "./types.js";

type HeartbeatWakeFn = (opts?: { reason?: string; coalesceMs?: number; sessionKey?: string }) => void;

function wakeGatewayAgent(
  requestHeartbeatNow: HeartbeatWakeFn | undefined,
  reason: string,
  logger: { info: (...args: unknown[]) => void; error: (...args: unknown[]) => void },
  sessionKey = "agent:main"
): void {
  if (typeof requestHeartbeatNow === "function") {
    try {
      requestHeartbeatNow({ reason, sessionKey });
      logger.info(`[perkos-a2a] Wake triggered via runtime.system.requestHeartbeatNow: ${reason} (session: ${sessionKey})`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      logger.error(`[perkos-a2a] runtime.system.requestHeartbeatNow failed: ${msg}`);
    }
  } else {
    logger.info("[perkos-a2a] runtime.system.requestHeartbeatNow unavailable — task will process on next agent turn");
  }
}

function openclawWakeSessionKey(config: A2APluginConfig): string {
  return config.runtime?.sessionKey || "agent:main";
}

function openclawEventSessionKey(config: A2APluginConfig): string {
  const key = config.runtime?.sessionKey;
  if (!key) return "agent:main:main";
  return key.endsWith(":main") ? key : `${key}:main`;
}

/** Extract a wallet address from a ChatIdentity ("user:0xabc..." → "0xabc..."). */
function parseWalletFromIdentity(identity: string): string | null {
  if (typeof identity !== "string" || !identity.startsWith("user:")) return null;
  const addr = identity.slice("user:".length);
  return addr.startsWith("0x") ? addr.toLowerCase() : null;
}

async function deliverToHermes(
  config: A2APluginConfig,
  message: string,
  logger: { info: (...args: unknown[]) => void; error: (...args: unknown[]) => void }
): Promise<void> {
  const runtime = config.runtime || {};
  const baseUrl = (runtime.hermesUrl || "http://127.0.0.1:8642").replace(/\/+$/, "");
  const endpoint = runtime.hermesEndpoint || "/v1/responses";
  const sessionKey = runtime.sessionKey || "a2a";
  const token = runtime.hermesToken;
  const url = `${baseUrl}${endpoint.startsWith("/") ? endpoint : `/${endpoint}`}`;
  const headers: Record<string, string> = {
    "content-type": "application/json",
    "x-hermes-session-id": sessionKey,
    "x-hermes-session-key": sessionKey,
  };
  if (token) headers.authorization = `Bearer ${token}`;

  const normalizedEndpoint = endpoint.replace(/^\/?/, "/");
  const body = normalizedEndpoint.startsWith("/v1/chat/completions")
    ? { model: "hermes-agent", messages: [{ role: "user", content: message }], stream: false }
    : normalizedEndpoint.startsWith("/v1/runs")
      ? { input: message, session_id: sessionKey }
      : normalizedEndpoint.startsWith("/v1/responses")
        ? { model: "hermes-agent", input: message, store: true }
        : { sessionKey, message };

  const response = await fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(`Hermes API delivery failed (${response.status}) at ${url}: ${text || response.statusText}`);
  }

  logger.info(`[perkos-a2a] Delivered inbound A2A task to Hermes API session ${sessionKey} via ${url}`);
}

export default function register(api: any) {
  const pluginConfig: A2APluginConfig = api.config?.plugins?.entries?.["perkos-a2a"]?.config || {
    agentName: "agent",
    port: 5050,
    skills: [],
    peers: {},
  };

  const logger = api.logger || console;
  const server = new A2AServer(pluginConfig, logger);

  // Pending tasks queue for optional hook-based injection/debugging
  const pendingTasks: Array<{ from: string; text: string; taskId: string; time: string }> = [];

  // Get system helpers from the plugin runtime API
  const enqueueSystemEvent = api.runtime?.system?.enqueueSystemEvent;
  const requestHeartbeatNow = api.runtime?.system?.requestHeartbeatNow as HeartbeatWakeFn | undefined;
  if (enqueueSystemEvent) {
    logger.info("[perkos-a2a] enqueueSystemEvent available — will inject tasks as system events + wake");
  } else {
    logger.info("[perkos-a2a] enqueueSystemEvent not available — falling back to before_agent_start hook only");
  }
  if (requestHeartbeatNow) {
    logger.info("[perkos-a2a] runtime.system.requestHeartbeatNow available — immediate wake enabled");
  } else {
    logger.info("[perkos-a2a] runtime.system.requestHeartbeatNow unavailable — wake will rely on next agent turn");
  }

  server.setTaskResultHandler(async (task, text) => {
    const runtimeKind = pluginConfig.runtime?.kind || "openclaw";
    const from = (task.metadata?.fromAgent as string) || "unknown";
    const marker = `[A2A_RESULT:${task.id}]`;
    const eventText = [
      marker,
      `From: ${from}`,
      `Task ID: ${task.id}`,
      `Context ID: ${task.contextId}`,
      "",
      "Execute the following request and include the final answer in your assistant reply.",
      `Your final answer must begin with exactly: ${marker}`,
      "Return the useful final answer after that marker.",
      "",
      text,
    ].join("\n");

    task.status = {
      state: "working",
      timestamp: new Date().toISOString(),
      message: {
        role: "agent",
        parts: [{ kind: "text", text: `Task dispatched to ${runtimeKind} runtime.` }],
      },
    };

    const handledAgentically = await tryHandleAgenticTask({
      selfName: pluginConfig.agentName,
      server,
      task,
      text,
      logger,
      runtimeResponder: (prompt, resultMarker) => generateRuntimeReply(pluginConfig, prompt, resultMarker),
    }).catch((err) => {
      const msg = err instanceof Error ? err.message : String(err);
      logger.error(`[perkos-a2a] Agentic task handler failed: ${msg}`);
      return false;
    });
    if (handledAgentically) return;

    if ((runtimeKind === "hermes" || runtimeKind === "hermes-api")) {
      await deliverToHermes(pluginConfig, eventText, logger);
      task.artifacts.push({
        kind: "artifact",
        artifactId: randomUUID(),
        parts: [{ kind: "text", text: `debug: delivered task ${task.id} to Hermes API Server` }],
      });
      const finalText = await generateRuntimeReply(pluginConfig, text, marker);
      completeTaskWithReply(task, finalText);
      return;
    }

    if (runtimeKind === "none") {
      task.artifacts.push({
        kind: "artifact",
        artifactId: randomUUID(),
        parts: [{ kind: "text", text: `debug: runtime delivery disabled for ${marker}` }],
      });
      return;
    }

    task.artifacts.push({
      kind: "artifact",
      artifactId: randomUUID(),
      parts: [{ kind: "text", text: `debug: enqueued system event for ${marker}` }],
    });

    if (enqueueSystemEvent) {
      enqueueSystemEvent(eventText, { sessionKey: openclawEventSessionKey(pluginConfig) });
    }

    const finalText = await generateRuntimeReply(pluginConfig, text, marker);
    completeTaskWithReply(task, finalText);

    wakeGatewayAgent(
      requestHeartbeatNow,
      `[A2A] Process queued task ${task.id}`,
      logger,
      openclawWakeSessionKey(pluginConfig)
    );
  });

  server.setTaskFailureHandler(async (task, errorText) => {
    task.status = {
      state: "failed",
      timestamp: new Date().toISOString(),
      message: {
        role: "agent",
        parts: [{ kind: "text", text: errorText }],
      },
    };
  });

  // Optional debug/session injection path, kept for visibility but no longer relied on for task completion.
  server.setMessageInjector((text: string, metadata?: Record<string, unknown>) => {
    const from = (metadata?.fromAgent as string) || "unknown";
    const taskId = (metadata?.taskId as string) || "unknown";
    const time = new Date().toISOString();

    pendingTasks.push({ from, text, taskId, time });
    logger.info(`[perkos-a2a] Task queued from ${from} (${taskId}), ${pendingTasks.length} pending`);

    const runtimeKind = pluginConfig.runtime?.kind || "openclaw";
    if (runtimeKind === "openclaw" && enqueueSystemEvent) {
      const eventText = [
        `[A2A OBSERVABILITY EVENT]`,
        `From: ${from} | Task ID: ${taskId} | Time: ${time}`,
        ``,
        text,
      ].join("\n");

      try {
        enqueueSystemEvent(eventText, { sessionKey: openclawEventSessionKey(pluginConfig) });
        logger.info(`[perkos-a2a] Observability system event enqueued for task ${taskId}`);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        logger.error(`[perkos-a2a] Failed to enqueue observability system event: ${msg}`);
      }
    }

    if (runtimeKind === "openclaw") {
      wakeGatewayAgent(
        requestHeartbeatNow,
        `[A2A] Incoming task from ${from} (${taskId}).`,
        logger,
        openclawWakeSessionKey(pluginConfig)
      );
    }
  });

  api.registerHook("before_agent_start", async () => {
    if (pendingTasks.length === 0) return {};

    const tasks = pendingTasks.splice(0, pendingTasks.length);
    const lines = [
      `[A2A OBSERVABILITY] ${tasks.length} inbound task(s) were received by perkos-a2a.`,
      "These notes are for visibility only. The actual task execution uses supported system-event processing.",
      "",
    ];
    for (const t of tasks) {
      lines.push(`--- From: ${t.from} | ID: ${t.taskId} | ${t.time} ---`);
      lines.push(t.text);
      lines.push("");
    }

    logger.info(`[perkos-a2a] Injecting ${tasks.length} observability note(s) into agent context via before_agent_start`);
    return { prependContext: lines.join("\n") };
  }, { name: "perkos-a2a-before-agent-start", description: "Inject pending A2A observability notes before agent starts." });

  api.runtime?.events?.onSessionTranscriptUpdate?.((update: any) => {
    const message = update?.message as any;
    const text = Array.isArray(message?.parts)
      ? message.parts
          .filter((p: any) => p?.kind === "text" && typeof p?.text === "string")
          .map((p: any) => p.text)
          .join("\n")
      : typeof message?.text === "string"
        ? message.text
        : "";

    if (!text) return;

    for (const task of server.getTasks()) {
      const marker = `[A2A_RESULT:${task.id}]`;
      if (!text.includes(marker)) continue;
      if (task.status.state === "completed") continue;
      const finalText = text.slice(text.indexOf(marker) + marker.length).trim();
      if (!finalText) continue;
      task.artifacts.push({
        kind: "artifact",
        artifactId: randomUUID(),
        parts: [{ kind: "text", text: finalText }],
      });
      task.status = {
        state: "completed",
        timestamp: new Date().toISOString(),
        message: {
          role: "agent",
          parts: [{ kind: "text", text: finalText }],
        },
      };
      logger.info(`[perkos-a2a] Task ${task.id} completed from transcript update`);
    }
  });

  // Start A2A server as background service
  api.registerService({
    id: "perkos-a2a",
    start: () => {
      server.start();
      logger.info(`[perkos-a2a] A2A server started for ${pluginConfig.agentName}`);
    },
    stop: () => {
      logger.info("[perkos-a2a] A2A server stopping");
    },
  });

  // ---------------------------------------------------------------------
  // PerkOS-Chat client (opt-in via pluginConfig.chat.enabled)
  //
  // When a user (or another agent) posts in a conv this agent participates
  // in, the chat server forwards a `chat_deliver` frame. We inject it as a
  // system event so the runtime wakes up and the LLM can decide to call
  // perkos_chat_reply.
  // ---------------------------------------------------------------------

  const chatApiKey = pluginConfig.chat?.apiKey || pluginConfig.relay?.apiKey;
  const chatClient = pluginConfig.chat?.enabled && chatApiKey
    ? new ChatClient({
      agentName: pluginConfig.agentName,
      config: {
        enabled: true,
        url: pluginConfig.chat?.url || "wss://chat.perkos.xyz/chat",
        apiKey: chatApiKey,
        storeRoot: pluginConfig.chat?.storeRoot,
        defaultHistoryLimit: pluginConfig.chat?.defaultHistoryLimit,
        minReconnectMs: pluginConfig.chat?.minReconnectMs,
        maxReconnectMs: pluginConfig.chat?.maxReconnectMs,
        heartbeatIntervalMs: pluginConfig.chat?.heartbeatIntervalMs,
      },
      handlers: {
        logger,
        onChatDeliver: async (frame: ChatDeliverFrame | ChatMessageInboundFrame) => {
          const walletAddress = parseWalletFromIdentity(frame.from);
          const marker = `[PERKOS_CHAT:${frame.convId}]`;
          const eventText = [
            marker,
            `From: ${frame.from}`,
            `Conversation: ${frame.convId}`,
            walletAddress ? `Wallet: ${walletAddress}` : "",
            "",
            "A user is messaging you in a PerkOS chat conversation.",
            "To respond, call the `perkos_chat_reply` tool with:",
            `  convId: "${frame.convId}"`,
            walletAddress ? `  walletAddress: "${walletAddress}"` : `  walletAddress: <derived from From>`,
            `  text: <your reply>`,
            "",
            "Message body:",
            frame.text,
          ].filter(Boolean).join("\n");

          const runtimeKind = pluginConfig.runtime?.kind || "openclaw";

          if (runtimeKind === "hermes" || runtimeKind === "hermes-api") {
            try {
              await deliverToHermes(pluginConfig, eventText, logger);
            } catch (err) {
              logger.error(`[perkos-chat] hermes delivery failed: ${err instanceof Error ? err.message : String(err)}`);
            }
            return;
          }

          if (runtimeKind === "none") {
            logger.info(`[perkos-chat] dropped chat_deliver for ${frame.convId} (runtime=none)`);
            return;
          }

          if (enqueueSystemEvent) {
            enqueueSystemEvent(eventText, { sessionKey: openclawEventSessionKey(pluginConfig) });
          }
          wakeGatewayAgent(
            requestHeartbeatNow,
            `[PerkOS-Chat] inbound message in ${frame.convId}`,
            logger,
            openclawWakeSessionKey(pluginConfig),
          );
        },
        onChannelJoin: async (frame: ChannelJoinFrame) => {
          logger.info(`[perkos-chat] joined conv ${frame.convId} (host: ${frame.historyHost}, ${frame.participants.length} participants)`);
        },
      },
    })
    : null;

  if (pluginConfig.chat?.enabled && !chatApiKey) {
    logger.error("[perkos-chat] chat.enabled=true but no apiKey (set chat.apiKey or fall back from relay.apiKey)");
  }

  if (chatClient) {
    api.registerService({
      id: "perkos-a2a-chat",
      start: () => {
        chatClient.start();
        logger.info(`[perkos-chat] client started for ${pluginConfig.agentName}`);
      },
      stop: () => {
        chatClient.stop();
        logger.info("[perkos-chat] client stopped");
      },
    });
  }

  // Tool: Send a task to a peer agent
  api.registerTool({
    name: "perkos_a2a_send",
    description:
      "Send a task to another agent via PerkOS A2A. " +
      "Use this to delegate work, ask a peer to research, or route a chain/circular workflow. " +
      "The message may name additional agents and a final notification target; the bridge preserves the route and prevents loops.",
    parameters: {
      type: "object",
      properties: {
        target: {
          type: "string",
          description:
            "Name of the target agent (e.g. 'mimir', 'tyr', 'bragi', 'idunn')",
        },
        message: {
          type: "string",
          description: "The task message to send. You can include routes like 'Apollo -> OpenClaw -> Hermes -> Morpheus' or natural language such as 'ask Hermes to research X, then notify Morpheus'.",
        },
      },
      required: ["target", "message"],
    },
    async execute(_id: string, params: { target: string; message: string }) {
      try {
        const result = await server.sendTask(params.target, params.message);
        if (result.error) {
          return {
            content: [
              {
                type: "text",
                text: `Failed to send task to ${params.target}: ${result.error.message}`,
              },
            ],
          };
        }
        const task = result.result as any;
        return {
          content: [
            {
              type: "text",
              text: [
                `Task sent to ${params.target} successfully.`,
                `Task ID: ${task?.id}`,
                `Status: ${task?.status?.state}`,
              ].join("\n"),
            },
          ],
        };
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        return {
          content: [{ type: "text", text: `Error sending task: ${msg}` }],
        };
      }
    },
  });

  // Tool: Discover peer agents
  api.registerTool({
    name: "perkos_a2a_discover",
    description:
      "Discover all peer agents in the council and their capabilities. " +
      "Returns each agent's status, name, description, and skills.",
    parameters: {
      type: "object",
      properties: {},
    },
    async execute() {
      try {
        const peers = await server.discoverPeers();
        const lines: string[] = ["## Council Agents\n"];
        for (const [name, info] of Object.entries(peers)) {
          if (info.status === "online" && info.card) {
            lines.push(`### ${info.card.name} (${name})`);
            lines.push(`- **Status:** online`);
            lines.push(`- **Description:** ${info.card.description}`);
            lines.push(
              `- **Skills:** ${info.card.skills.map((s) => s.name).join(", ")}`
            );
            lines.push("");
          } else {
            lines.push(`### ${name}`);
            lines.push(`- **Status:** offline`);
            lines.push("");
          }
        }
        return {
          content: [{ type: "text", text: lines.join("\n") }],
        };
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        return {
          content: [{ type: "text", text: `Error discovering peers: ${msg}` }],
        };
      }
    },
  });

  // Tool: Get task status
  api.registerTool({
    name: "perkos_a2a_status",
    description: "Get the status of a previously sent A2A task by its ID.",
    parameters: {
      type: "object",
      properties: {
        target: {
          type: "string",
          description: "Name of the agent that received the task",
        },
        taskId: {
          type: "string",
          description: "The task ID returned from perkos_a2a_send",
        },
      },
      required: ["target", "taskId"],
    },
    async execute(
      _id: string,
      params: { target: string; taskId: string }
    ) {
      const targetUrl = pluginConfig.peers[params.target];
      if (!targetUrl) {
        return {
          content: [
            { type: "text", text: `Unknown agent: ${params.target}` },
          ],
        };
      }

      try {
        const headers: Record<string, string> = { "Content-Type": "application/json" };
        const peerAuth = pluginConfig.peerAuth?.[params.target];
        if (peerAuth) {
          headers["x-api-key"] = peerAuth;
        }

        const response = await fetch(`${targetUrl}/a2a/jsonrpc`, {
          method: "POST",
          headers,
          body: JSON.stringify({
            jsonrpc: "2.0",
            method: "tasks/get",
            id: crypto.randomUUID(),
            params: { id: params.taskId },
          }),
        });

        const data = (await response.json()) as any;
        if (data.error) {
          return {
            content: [
              { type: "text", text: `Error: ${data.error.message}` },
            ],
          };
        }

        const task = data.result;
        return {
          content: [
            {
              type: "text",
              text: [
                `**Task:** ${task.id}`,
                `**Status:** ${task.status.state}`,
                `**Updated:** ${task.status.timestamp}`,
                task.artifacts?.length
                  ? `**Artifacts:** ${task.artifacts.length}`
                  : "",
              ]
                .filter(Boolean)
                .join("\n"),
            },
          ],
        };
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        return {
          content: [{ type: "text", text: `Error: ${msg}` }],
        };
      }
    },
  });

  // ---------------------------------------------------------------------
  // Chat tools (only meaningful when chat is enabled; the tools still
  // register so the agent's tool surface is stable, but they return an
  // error if the chat client isn't running).
  // ---------------------------------------------------------------------

  api.registerTool({
    name: "perkos_chat_reply",
    description:
      "Send a reply into a PerkOS chat conversation. Use this when a user " +
      "(or another agent) sent a chat message you should respond to. " +
      "The system event that woke you contains `Conversation: <convId>` and " +
      "`Wallet: <walletAddress>` lines you can copy directly. The reply is " +
      "appended to local history first, then sent on the wire — so it's " +
      "preserved even if the chat server is briefly unreachable.",
    parameters: {
      type: "object",
      properties: {
        convId: {
          type: "string",
          description: "Conversation id (UUID) — found in the [PERKOS_CHAT:<convId>] marker.",
        },
        walletAddress: {
          type: "string",
          description: "Wallet that owns the conversation. From the `Wallet:` line of the system event.",
        },
        text: {
          type: "string",
          description: "Reply body. Markdown supported.",
        },
        replyTo: {
          type: "string",
          description: "Optional id of the inbound message you are replying to.",
        },
      },
      required: ["convId", "walletAddress", "text"],
    },
    async execute(_id: string, params: { convId: string; walletAddress: string; text: string; replyTo?: string }) {
      if (!chatClient) {
        return {
          content: [{ type: "text", text: "PerkOS-Chat client is not enabled in this agent's config." }],
        };
      }
      try {
        const result = await chatClient.sendReply({
          convId: params.convId,
          walletAddress: params.walletAddress,
          text: params.text,
          replyTo: params.replyTo ?? null,
        });
        return {
          content: [{
            type: "text",
            text: [
              `Reply ${result.delivered ? "delivered" : "queued (offline)"}.`,
              `Message id: ${result.id}`,
              `Conv: ${params.convId}`,
            ].join("\n"),
          }],
        };
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        return { content: [{ type: "text", text: `Error sending chat reply: ${msg}` }] };
      }
    },
  });

  api.registerTool({
    name: "perkos_chat_history",
    description:
      "Read recent messages from a PerkOS chat conversation (this agent's " +
      "local store). Returns up to `limit` messages older than `before` " +
      "(ISO timestamp), most-recent-first ordered by timestamp. Use this to " +
      "recall context before composing a reply.",
    parameters: {
      type: "object",
      properties: {
        convId: { type: "string", description: "Conversation id (UUID)." },
        before: {
          type: "string",
          description: "Optional ISO timestamp; only messages strictly older than this are returned. Omit to get the newest page.",
        },
        limit: {
          type: "number",
          description: "Max messages to return (1-500, default 50).",
        },
      },
      required: ["convId"],
    },
    async execute(_id: string, params: { convId: string; before?: string; limit?: number }) {
      if (!chatClient) {
        return {
          content: [{ type: "text", text: "PerkOS-Chat client is not enabled in this agent's config." }],
        };
      }
      try {
        const store = chatClient.getStore();
        const page = await store.readPage(params.convId, {
          before: params.before ?? null,
          limit: params.limit ?? 50,
        });
        if (page.messages.length === 0) {
          return { content: [{ type: "text", text: `No messages found in ${params.convId}.` }] };
        }
        const lines = page.messages.map((m) => `[${m.timestamp}] ${m.from}: ${m.text}`);
        if (page.hasMore) lines.push("", "(older messages available — call again with `before` set to the earliest timestamp)");
        return { content: [{ type: "text", text: lines.join("\n") }] };
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        return { content: [{ type: "text", text: `Error reading chat history: ${msg}` }] };
      }
    },
  });

  // Gateway RPC method
  api.registerGatewayMethod("perkos-a2a.status", ({ respond }: any) => {
    respond(true, {
      agent: pluginConfig.agentName,
      port: pluginConfig.port,
      mode: pluginConfig.mode || "auto",
      clientOnly: server.isClientOnly(),
      relayConnected: server.isRelayConnected(),
      relayUrl: pluginConfig.relay?.url || null,
      peers: Object.keys(pluginConfig.peers),
      pendingTasks: pendingTasks.length,
      hasSystemEventInjection: !!enqueueSystemEvent,
      protocol: "a2a",
      version: "0.8.4",
      chat: chatClient
        ? {
          enabled: true,
          connected: chatClient.isConnected(),
          url: pluginConfig.chat?.url || "wss://chat.perkos.xyz/chat",
        }
        : { enabled: false, connected: false, url: null },
    });
  });

  // CLI command
  api.registerCli(
    ({ program }: any) => {
      const cmd = program.command("perkos-a2a").description("PerkOS A2A protocol tools");

      cmd
        .command("status")
        .description("Show A2A agent status")
        .action(async () => {
          console.log(`Agent: ${pluginConfig.agentName}`);
          console.log(`Port: ${pluginConfig.port}`);
          console.log(`Mode: ${pluginConfig.mode || "auto"}`);
          console.log(`Client-only: ${server.isClientOnly()}`);
          console.log(`Relay connected: ${server.isRelayConnected()}`);
          console.log(`Relay URL: ${pluginConfig.relay?.url || "(not configured)"}`);
          console.log(`Auth required: ${pluginConfig.auth?.requireApiKey || false}`);
          console.log(`Peers: ${Object.keys(pluginConfig.peers).join(", ") || "(none)"}`);
          console.log(`System event injection: ${!!enqueueSystemEvent}`);
          if (chatClient) {
            console.log(`Chat: enabled (connected=${chatClient.isConnected()}, url=${pluginConfig.chat?.url || "wss://chat.perkos.xyz/chat"})`);
          } else {
            console.log("Chat: disabled");
          }
        });

      cmd
        .command("discover")
        .description("Discover peer agents")
        .action(async () => {
          const peers = await server.discoverPeers();
          for (const [name, info] of Object.entries(peers)) {
            console.log(
              `${name}: ${info.status}${info.card ? ` -- ${info.card.description}` : ""}`
            );
          }
        });

      cmd
        .command("send <target> <message>")
        .description("Send a task to a peer agent")
        .action(async (target: string, message: string) => {
          const result = await server.sendTask(target, message);
          console.log(JSON.stringify(result, null, 2));
        });

      cmd
        .command("setup")
        .description("Detect networking environment and show recommendations")
        .action(async () => {
          console.log("[perkos-a2a] Detecting environment...\n");

          const isMac = process.platform === "darwin";
          console.log(`Platform: ${process.platform}${isMac ? " (macOS)" : ""}`);

          const net = await detectNetworking();
          console.log(`Public IP: ${net.publicIp || "unknown"}`);
          console.log(`Local IPs: ${net.localIps.join(", ") || "none"}`);
          console.log(`Behind NAT: ${net.isBehindNat ? "yes" : "no"}`);
          console.log(`Tailscale: ${net.hasTailscale ? "installed" : "not found"}${net.tailscaleIp ? ` (${net.tailscaleIp})` : ""}`);

          let portAvailable = true;
          try {
            const netMod = await import("net");
            await new Promise<void>((resolve, reject) => {
              const srv = netMod.createServer();
              srv.once("error", (err: NodeJS.ErrnoException) => {
                if (err.code === "EADDRINUSE") {
                  portAvailable = false;
                  resolve();
                } else {
                  reject(err);
                }
              });
              srv.listen(pluginConfig.port, () => {
                srv.close(() => resolve());
              });
            });
          } catch {
            // ignore
          }
          console.log(`Port ${pluginConfig.port}: ${portAvailable ? "available" : "IN USE"}`);
          console.log(`\nRelay: ${pluginConfig.relay?.enabled ? "enabled" : "disabled"}`);
          if (pluginConfig.relay?.url) {
            console.log(`Relay URL: ${pluginConfig.relay.url}`);
          }
          console.log(`Auth: ${pluginConfig.auth?.requireApiKey ? "enabled" : "disabled"}`);
          console.log(`System event injection: ${!!enqueueSystemEvent}`);

          console.log("\n--- Recommendations ---\n");

          console.log("Same-host / same-IP rule:");
          console.log("  IP address is transport only, not agent identity.");
          console.log("  If multiple agents share this host/LAN/public IP, assign unique agentName + port.");
          console.log(`  Local loopback URL for this agent: http://127.0.0.1:${pluginConfig.port}/a2a/jsonrpc`);
          console.log("  Configure same-host peers as http://127.0.0.1:<peer-port>, not by shared public IP.");
          console.log("");

          if (!net.isBehindNat) {
            console.log("You have a public IP. Use it only for remote peers that cannot reach a local/tunnel URL.");
            console.log(`  Public A2A URL candidate: http://${net.publicIp}:${pluginConfig.port}/a2a/jsonrpc`);
          } else if (pluginConfig.relay?.enabled) {
            console.log("You are behind NAT but relay is configured. Bidirectional communication via relay.");
          } else if (net.hasTailscale && net.tailscaleIp) {
            console.log("Use your Tailscale IP for peers.");
            console.log(`  Your A2A URL: http://${net.tailscaleIp}:${pluginConfig.port}/a2a/jsonrpc`);
          } else {
            console.log("You are behind NAT. Options:");
            console.log("  1) Configure a relay hub (recommended) - set relay.url and relay.enabled in config");
            console.log("  2) Install Tailscale - https://tailscale.com");
            console.log("  3) Set up a Cloudflare Tunnel");
            console.log('  4) Client-only mode (can send but not receive) - set mode: "client-only"');
          }

          console.log("\n--- Current Config ---\n");
          console.log(`Agent: ${pluginConfig.agentName}`);
          console.log(`Port: ${pluginConfig.port}`);
          console.log(`Mode: ${pluginConfig.mode || "auto"}`);
          console.log(`Peers: ${JSON.stringify(pluginConfig.peers, null, 2)}`);
        });
    },
    { commands: ["perkos-a2a"] }
  );
}
